import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { THEME_COLOR } from '../../../theme/formStyles';
import {
    dayNumber, engineerName, fmtDate, fmtDuration, fmtTime, groupBySite, isOnDay, isOpenVisit, isUnassigned, monthDays,
    itemKey, siteColor, subTaskStatusStyle, taskLength, todayYmd, visitKey, visitMinutes, visitsFor,
} from '../projectSchedule.utils';

function DayPosition({ task, ymd }) {
    const len = taskLength(task);
    if (len === 1) return null;
    const style = { fontSize: '11px', fontWeight: 700, borderRadius: '5px', px: 0.75, whiteSpace: 'nowrap' };
    if (ymd === task.start_date) return <Box sx={{ ...style, bgcolor: '#e7f6ec', color: '#15803d' }}>Starts</Box>;
    if (ymd === task.end_date) return <Box sx={{ ...style, bgcolor: '#fff1e6', color: '#c2410c' }}>Ends</Box>;
    return <Box sx={{ ...style, bgcolor: '#eef1f4', color: '#6b7785' }}>Day {dayNumber(task, ymd)}/{len}</Box>;
}

// "in 9:20 am · out 5:15 pm · 7h 55m", or a live "on site" marker, for each check-in that day
function VisitLine({ visits }) {
    if (!visits.length) return null;
    return visits.map(v => (
        <Typography key={v.id} component="span" sx={{ display: 'block', fontSize: '12px', color: '#6b7785' }}>
            {visits.length > 1 && `${v.user_name}: `}in {fmtTime(v.check_in_at)}
            {isOpenVisit(v)
                ? <> · <span style={{ color: '#16a34a', fontWeight: 700 }}>● on site</span></>
                : ` · out ${fmtTime(v.check_out_at)} · ${fmtDuration(visitMinutes(v))}`}
        </Typography>
    ));
}

export function TaskRow({ task, ymd, onOpen, visits = [] }) {
    return (
        <Box onClick={() => onOpen(task)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.1, borderTop: '1px solid #e5e9ee', cursor: 'pointer', '&:hover': { bgcolor: '#f9fbfc' } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500, fontSize: '13.5px' }}>{task.sub_task_name}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>
                    <span style={isUnassigned(task) ? { color: '#dc2626', fontWeight: 700 } : undefined}>{engineerName(task)}</span>
                    {task.task_name && ` · ${task.task_name}`}
                </Typography>
                <VisitLine visits={visitsFor(visits, task, ymd)} />
            </Box>
            <DayPosition task={task} ymd={ymd} />
            <Chip size="small" label={task.status} sx={{ fontWeight: 700, fontSize: '11px', height: 22, ...subTaskStatusStyle(task.status) }} />
        </Box>
    );
}

export function SiteTaskGroup({ group, ymd, onOpen, visits }) {
    return (
        <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 3, mb: 1, overflow: 'hidden', bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: '#fafbfc', fontWeight: 500, fontSize: '13px' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: siteColor(group.facility_id), flex: 'none' }} />
                {group.facility_name}
                <Typography component="span" sx={{ ml: 'auto', color: '#6b7785', fontSize: '12px' }}>{group.tasks.length}</Typography>
            </Box>
            {group.tasks.map(t => <TaskRow key={itemKey(t)} task={t} ymd={ymd} onOpen={onOpen} visits={visits} />)}
        </Box>
    );
}

export function TodayStats({ tasks, visits = [] }) {
    const today = todayYmd();
    const todays = tasks.filter(t => isOnDay(t, today));
    const todayKeys = new Set(todays.map(itemKey));
    const engineers = new Set(todays.filter(t => !isUnassigned(t)).map(t => t.spoc.id || t.spoc.name)).size;
    const onSiteNow = new Set(visits.filter(v => v.visit_date === today && isOpenVisit(v) && todayKeys.has(visitKey(v))).map(v => v.user_id)).size;
    const unassigned = todays.filter(isUnassigned).length;
    const stats = [
        { value: todays.length, label: 'jobs today' },
        { value: engineers, label: 'engineers out' },
        { value: onSiteNow, label: 'on site now', live: onSiteNow > 0 },
        { value: unassigned, label: 'unassigned', warn: unassigned > 0 },
    ];
    // Green gradient panel with translucent tiles, as in the v69 design; unassigned work gets a red tint
    return (
        <Box sx={{
            display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', sm: 'repeat(4, minmax(0,1fr))' }, gap: 1.25, p: 1.75, mt: 1, mb: 1.75, borderRadius: 4,
            background: 'radial-gradient(120% 140% at 100% 0%, #1fc79b 0%, #0e7a63 50%, #0b4f42 100%)', boxShadow: '0 8px 22px rgba(14,122,99,.22)',
        }}>
            {stats.map(s => (
                <Box key={s.label} sx={{ bgcolor: s.warn ? 'rgba(255,120,120,.28)' : 'rgba(255,255,255,.13)', borderRadius: 3, px: 1.5, py: 1.25, color: '#fff' }}>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{s.value}</Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'rgba(255,255,255,.85)' }}>{s.label}</Typography>
                </Box>
            ))}
        </Box>
    );
}

function DayBlock({ ymd, tasks, onOpen, title, visits }) {
    const dayTasks = tasks.filter(t => isOnDay(t, ymd));
    if (!dayTasks.length) return null;
    const groups = groupBySite(dayTasks);
    const isToday = ymd === todayYmd();
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mt: 2, mb: 1 }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: isToday ? THEME_COLOR : '#1f2933' }}>
                    {title || fmtDate(ymd, 'dddd D MMM')}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>
                    {dayTasks.length} task{dayTasks.length === 1 ? '' : 's'} · {groups.length} site{groups.length === 1 ? '' : 's'}
                </Typography>
            </Box>
            {groups.map(g => <SiteTaskGroup key={g.facility_id} group={g} ymd={ymd} onOpen={onOpen} visits={visits} />)}
        </Box>
    );
}

const sectionTitleSx = { fontSize: '12px', fontWeight: 700, color: '#6b7785', textTransform: 'uppercase', letterSpacing: '.05em', mt: 2.5, mb: 0.5 };

// Day-by-day list grouped by site. In the current month it opens on today, then coming up, then earlier days folded away.
export default function DayListView({ tasks, visits, year, month, onOpenTask }) {
    const days = monthDays(year, month);
    const today = todayYmd();
    const isCurrentMonth = days.includes(today);

    if (!tasks.length) {
        return <Typography sx={{ textAlign: 'center', color: '#6b7785', fontSize: '13px', py: 4 }}>No sub-tasks scheduled this month.</Typography>;
    }

    if (!isCurrentMonth) {
        return <Box>{days.map(d => <DayBlock key={d} ymd={d} tasks={tasks} onOpen={onOpenTask} visits={visits} />)}</Box>;
    }

    const hasTasksOn = (d) => tasks.some(t => isOnDay(t, d));
    const upcomingDays = days.filter(d => d > today && hasTasksOn(d));
    const earlierDays = days.filter(d => d < today && hasTasksOn(d)).reverse();
    const hasToday = tasks.some(t => isOnDay(t, today));

    return (
        <Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: THEME_COLOR, mt: 2 }}>Today · {fmtDate(today, 'dddd D MMM')}</Typography>
            <TodayStats tasks={tasks} visits={visits} />
            {hasToday
                ? groupBySite(tasks.filter(t => isOnDay(t, today))).map(g => <SiteTaskGroup key={g.facility_id} group={g} ymd={today} onOpen={onOpenTask} visits={visits} />)
                : <Typography sx={{ textAlign: 'center', color: '#6b7785', fontSize: '13px', py: 1.5 }}>No jobs today for this selection.</Typography>}

            {upcomingDays.length > 0 && <Typography sx={sectionTitleSx}>Coming up</Typography>}
            {upcomingDays.map(d => <DayBlock key={d} ymd={d} tasks={tasks} onOpen={onOpenTask} visits={visits} />)}

            {earlierDays.length > 0 && (
                <Accordion disableGutters sx={{ mt: 2.5, border: '1px solid #e5e9ee', boxShadow: 'none', borderRadius: '12px !important', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 500, fontSize: '13.5px' }}>
                            Earlier this month <span style={{ color: '#6b7785', fontWeight: 400 }}>· {earlierDays.length} day{earlierDays.length === 1 ? '' : 's'}</span>
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        {earlierDays.map(d => <DayBlock key={d} ymd={d} tasks={tasks} onOpen={onOpenTask} visits={visits} />)}
                    </AccordionDetails>
                </Accordion>
            )}
        </Box>
    );
}
